import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef,
} from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { capitalize } from '@core/util/string';
import { Context, MatterFileType } from '@models/matter';
import { DocumentLocation } from '@models/documents';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import {
	TagMapping,
	TagsMapping,
	TagsMappingMap,
} from '@state/global/tags/mappings/tags-mapping';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format, isValid, parse } from 'date-fns';
import jsPDF from 'jspdf';
import { RowInput, UserOptions } from 'jspdf-autotable';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { TagsService } from 'src/app/shared/services/tags/tags.service';
import { Category, TagVM } from './tags.interfaces';

interface jsPDFWithPlugin extends jsPDF {
	autoTable: (options: UserOptions) => jsPDF;
}

interface ITableRow {
	/**
	 * @description holds the name of the category with tags post fixed.
	 */
	category: string;
	tags: RowInput[];
}

export interface TagsDefinitionDialogData {
	location: DocumentLocation;
	context?: Context;
}

export const noTagDescription =
	'There is no description set for this tag at the moment.';
export const noTagValue = 'There is no value set for this tag at the moment.';

export interface FlatList {
	name: string;
	type: 'category' | 'tag';
	variations?: { value: string; label: string }[];
	description?: string;
	value?: string;
	dummyValue?: string;
	fileTypes: MatterFileType[];
}

@Component({
	selector: 'app-tags-definition',
	templateUrl: './tags-definition-dialog.component.html',
	styleUrls: ['./tags-definition-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TagsDefinitionDialogComponent implements OnInit {
	public ICON = ICON;
	public location: DocumentLocation;
	public noTagDescription: string;
	public noTagValue: string;
	public searchValue: string;
	public searchValues: FlatList[];
	public seeVariations: boolean;
	public selectedTag: FlatList;
	public tags: TagVM[];
	public tagDescription: string;
	public tagValue: string;
	public tagValueExists: boolean;
	public tagMapping: TagMapping[];

	public helpers: string[] = [
		'default',
		'lowercase',
		'uppercase',
		'capital',
		'day',
		'month',
		'year',
	];
	public helperOptions = [
		'default',
		'lowercase',
		'uppercase',
		'capital',
		'day',
		'month',
		'year',
	].map((helper) => ({
		name: capitalize(helper),
		value: helper,
	}));
	public selectedHelper: string;
	public flatCategories: FlatList[] = [];
	public showDetails = false;
	public FileType = MatterFileType;
	public TagMappingMap = TagsMappingMap;
	public filteredCategories$: Observable<Category[]>;
	public context: Context;
	public recentSearchValue$: BehaviorSubject<string> =
		new BehaviorSubject<string>('');
	public selectedCategory$: Observable<Category>;
	public selectedCategory: Category;
	public flatCategories$: Observable<FlatList[]>;
	public rowHeightPx = 38.4;

	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	ngOnInit(): void {
		this.location = this.data.location;
		this.context = this.data.context;
		this.noTagDescription = noTagDescription;
		this.noTagValue = noTagValue;
		this.seeVariations = false;
		this.selectedHelper = '';
		this.tagValueExists = false;

		this.filteredCategories$ = combineLatest([
			this.recentSearchValue$,
			this.tagsService.getMappedTagsAndCategories(this.location, this.context),
		]).pipe(
			map(([recentSearchValue, categories]) =>
				this.searchValueChanged(recentSearchValue, categories),
			),
			shareReplay(1),
		);

		this.tagMapping = cloneDeep(TagsMapping);
	}

	getCategoryPath(categoryName: string, parentPath = ''): string {
		const separator = parentPath ? ' / ' : '';
		return `${parentPath}${separator}${categoryName}`;
	}

	createFlatList(selectedCategory: Category, parentCategoryName?: string) {
		if (!selectedCategory) return [];

		const flatList: FlatList[] = [];

		const newPath = this.getCategoryPath(
			selectedCategory.name,
			parentCategoryName,
		);

		if (selectedCategory.tags && selectedCategory.tags.length > 0) {
			flatList.push({
				name: newPath,
				type: 'category',
				fileTypes: [
					MatterFileType.SALE,
					MatterFileType.PURCHASE,
					MatterFileType.REFINANCE,
				],
			});

			for (const tag of selectedCategory.tags) {
				flatList.push({
					name: tag.tag,
					type: 'tag',
					variations: tag.variations,
					description: tag.description,
					value: tag.value,
					dummyValue: tag.dummyValue,
					fileTypes: tag.fileTypes,
				});
			}
		}

		if (
			selectedCategory.subCategory &&
			selectedCategory.subCategory.length > 0
		) {
			for (const subCategory of selectedCategory.subCategory) {
				flatList.push(...this.createFlatList(subCategory, newPath));
			}
		}

		return flatList;
	}

	constructor(
		private dialogRef: MatDialogRef<TagsDefinitionDialogComponent>,
		private tagsService: TagsService,
		private navService: NavigationService,
		@Inject(MAT_DIALOG_DATA) public data: TagsDefinitionDialogData,
		public dialog: MatDialog,
	) {}

	selectCategory(category: Category, root?: boolean): void {
		if (root) {
			this.selectTag(null);

			if (this.selectedCategory === category) {
				this.selectedCategory = null;
				this.flatCategories = [];
				return;
			}
		}

		if (this.selectedCategory !== category) {
			this.selectTag(null);
		}
		this.selectedCategory = category;
		this.flatCategories = this.createFlatList(category);
	}

	selectTag(tag: FlatList): void {
		this.selectedTag = this.flatCategories.find((x) => x.name === tag?.name);
		this.selectedHelper = 'default';
		this.setTagHelperValue();
		this.showDetails = true;
	}

	selectHelper(selectedHelper: DropdownResult<DropdownOption<string>>): void {
		this.selectedHelper = selectedHelper.result.value;
		this.setTagHelperValue();
	}

	cancelModal(): void {
		this.dialogRef.close(false);
	}

	insertTag(): void {
		if (this.selectedHelper === 'default' || !this.selectedHelper) {
			this.dialogRef.close(this.selectedTag.name);
		} else {
			this.dialogRef.close(
				this.getTagWithHelpers(this.selectedTag.name, this.selectedHelper),
			);
		}
	}

	resetSelectionIfNotInCategories(categories: Category[]): void {
		// Update selected categories to match the new instances in searchValues
		if (this.selectedCategory) {
			const matchingCategory = categories.find(
				(category) => category.name === this.selectedCategory.name,
			);
			if (matchingCategory) {
				this.selectedCategory = matchingCategory;
			} else {
				this.selectedCategory = null;
			}

			this.flatCategories = this.createFlatList(this.selectedCategory);
		}
	}

	searchValueChanged(value: string, categories: Category[]): Category[] {
		this.searchValue = value;
		if (!this.selectedCategory && value) {
			this.selectCategory(
				categories.find((category) =>
					category.name.toLowerCase().includes('all'),
				),
				true,
			);
		}
		const searchValueLower = value.toLowerCase();

		const categoriesCopy = cloneDeep(categories);

		const filterCategories = (categories: Category[]) => {
			return categories.filter((category) => {
				let categoryMatch = false;
				let subCategoryMatch = false;

				if (category.name.toLowerCase().includes(searchValueLower)) {
					categoryMatch = true;
				}

				if (category.subCategory) {
					category.subCategory = filterCategories(category.subCategory);
					subCategoryMatch = category.subCategory.length > 0;
				}

				// Filter tags in category
				category.tags = category.tags.filter((category) =>
					category.tag.toLowerCase().includes(searchValueLower),
				);

				return categoryMatch || subCategoryMatch || category.tags.length > 0;
			});
		};

		if (value === '') {
			const filteredCategories = categoriesCopy;
			this.resetSelectionIfNotInCategories(filteredCategories);
			return filteredCategories;
		}

		const filteredCategories = filterCategories(categoriesCopy);
		this.resetSelectionIfNotInCategories(filteredCategories);
		return filteredCategories;
	}

	getLabelId(label: string): string {
		return label.replace(/\./g, '');
	}

	getTagWithHelpers(tag: string, variation: string): string {
		if (variation === 'default') {
			return tag;
		}

		return `${variation} ${tag}`;
	}

	setTagHelperValue(): void {
		this.tagDescription =
			this.selectedTag?.description === 'undefined'
				? this.noTagDescription
				: this.selectedTag?.description;

		this.tagValue =
			this.location === 'business'
				? this.selectedTag?.dummyValue
				: this.selectedTag?.value;
		if (this.tagValue === '' || this.tagValue === 'undefined') {
			this.tagValue = this.noTagValue;
			this.tagValueExists = false;

			return;
		}

		this.tagValueExists = true;

		if (
			['day', 'month', 'year'].includes(this.selectedHelper) &&
			isValid(parse(this.tagValue, 'MMMM do, y', new Date()))
		) {
			let dateFormat = 'yyyy';
			switch (this.selectedHelper) {
				case 'month':
					dateFormat = 'MMMM';
					break;
				case 'day':
					dateFormat = 'd';
					break;
			}
			this.tagValue = format(
				parse(this.tagValue, 'MMMM do, y', new Date()),
				dateFormat,
			);
		} else {
			switch (this.selectedHelper) {
				case 'capital':
					this.tagValue = `${this.tagValue[0]?.toUpperCase()}${this.tagValue.slice(1)}`;
					break;
				case 'lowercase':
					this.tagValue = this.tagValue.toLowerCase();
					break;
				case 'uppercase':
					this.tagValue = this.tagValue.toUpperCase();
					break;
			}
		}
	}

	printTagsTable() {
		this.filteredCategories$
			.pipe(
				take(1),
				map((categories) => categories.filter((c) => c.name !== 'All')),
			)
			.subscribe((filteredCategories) => {
				const doc = new jsPDF({
					orientation: 'landscape',
					unit: 'mm',
				}) as jsPDFWithPlugin;

				const tableRows = filteredCategories.reduce((acc, category) => {
					if (category.name !== 'All') {
						const subCategoryTags = category.subCategory
							? category.subCategory.map((s) => this.createFlatList(s))
							: [];
						const _subCategoryTags = [];

						for (const s of subCategoryTags) {
							for (const t of s) {
								_subCategoryTags.push({
									tag: t.name,
									description: t.description,
								});
							}
						}

						acc.push({
							category: `${category.name} Tags`,
							tags: category.tags
								.concat(_subCategoryTags)
								.map((tag) => [tag.tag, tag.description]),
						});
					}
					return acc;
				}, [] as ITableRow[]);

				doc.setFontSize(8);
				doc.setFont(undefined, 'bold');

				let startY = 18;
				let pageCount = 0;

				const addFooter = () => {
					pageCount++;
				};

				for (const row of tableRows) {
					doc.autoTable({
						startY: startY,
						head: [[row.category, 'Description']],
						body: row.tags,
						headStyles: { fillColor: '044321' },
						pageBreak: 'avoid',
						columnStyles: {
							0: { cellWidth: 100 },
						},
						didDrawPage: addFooter,
					});
					startY += row.tags.length * 8 + 20;
				}

				// Now, loop through and add the footers
				for (let i = 1; i <= pageCount; i++) {
					doc.setPage(i);
					const pageWidth = doc.internal.pageSize.getWidth();
					const fontSize = doc.getFontSize();
					const text = `Closer Tag Dictionary - page ${i} of ${pageCount}`;
					const txtWidth =
						(doc.getStringUnitWidth(text) * fontSize) /
						doc.internal.scaleFactor;
					const x = (pageWidth - txtWidth) / 2;
					doc.text(text, x, 200);
				}

				doc.autoPrint();
				this.navService.goToRemoteUrl(doc.output('bloburl').toString(), true);
			});
	}

	onSearchChange(value: string): void {
		this.recentSearchValue$.next(value);
	}
}
